import type {AudioItem} from '../../vendor/src/features/remotion-editor/editor/items/audio/audio-item-type';
import type {EditorStarterItem} from '../../vendor/src/features/remotion-editor/editor/items/item-type';
import type {TrackType} from '../../vendor/src/features/remotion-editor/editor/state/types';

const GAP_TOLERANCE_FRAMES = 5;

export const isTtsAudioItem = (item: EditorStarterItem): item is AudioItem =>
  item.type === 'audio' && !!item.renderMeta?.vendorModel;

/** The exact item groups rendered by the global TTS audio layer. */
export function collectTtsAudioGroups(
  items: Record<string, EditorStarterItem>,
  tracks: TrackType[],
  fps: number,
): Array<{assetId: string; items: AudioItem[]; trackMuted: boolean}> {
  const assetMap = new Map<string, {items: AudioItem[]; trackMuted: boolean}>();

  // Only top-level track items participate. The first track that contributes an
  // asset owns its mute state, even if later items come from another track.
  for (const track of tracks) {
    for (const itemId of track.items) {
      const item = items[itemId];
      if (!item || !isTtsAudioItem(item)) continue;

      const existing = assetMap.get(item.assetId);
      if (existing) {
        existing.items.push(item);
      } else {
        assetMap.set(item.assetId, {items: [item], trackMuted: track.muted});
      }
    }
  }

  const groups: Array<{assetId: string; items: AudioItem[]; trackMuted: boolean}> = [];
  const sourceToleranceSeconds = GAP_TOLERANCE_FRAMES / fps;

  for (const [assetId, {items: audioItems, trackMuted}] of assetMap) {
    if (audioItems.length < 2) continue;

    const sorted = [...audioItems].sort((a, b) => a.from - b.from);
    let current: AudioItem[] = [sorted[0]!];

    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1]!;
      const next = sorted[i]!;
      const timelineGap = next.from - (prev.from + prev.durationInFrames);
      const previousSourceEnd = prev.audioStartFromInSeconds +
        (prev.durationInFrames / fps) * prev.playbackRate;
      const sourceGap = Math.abs(next.audioStartFromInSeconds - previousSourceEnd);

      if (timelineGap > GAP_TOLERANCE_FRAMES || sourceGap > sourceToleranceSeconds) {
        if (current.length >= 2) groups.push({assetId, items: current, trackMuted});
        current = [next];
      } else {
        current.push(next);
      }
    }

    if (current.length >= 2) groups.push({assetId, items: current, trackMuted});
  }

  return groups;
}
